<template>
  <div>
    <div class="container pt-125 md:pt-175 lg:pt-200">
      <breadcrumb
        :breadcrumbs="[
          !showCategoryFilter && products?.category ? { uri: variables.uri.split('/')[0], title: variables.uri.split('/')[0] } : null,
          (products?.entry || (!showCategoryFilter && products?.category)),
        ].filter((b) => b)"
      />
    </div>

    <div class="container mt-25">
      <template v-if="products?.entry">
        <h1
          v-if="products?.entry.heading"
          class="wysiwyg-title text-xl md:text-2xl lg:text-3xl xl:text-4xl mb-25"
          v-html="products.entry.heading"
        />
        <div
          v-if="products?.entry.intro"
          class="wysiwyg max-w-4xl mb-25"
          v-html="products.entry.intro"
        />
      </template>
      <template v-else-if="products?.category">
        <h1
          v-if="products?.category.heading"
          class="wysiwyg-title text-xl md:text-2xl lg:text-3xl xl:text-4xl mb-25"
          v-html="products.category.heading"
        />
        <div
          v-if="products?.category.intro"
          class="wysiwyg max-w-4xl mb-25"
          v-html="products.category.intro"
        />
      </template>

      <div class="flex justify-between">
        <div class="relative">
          <button
            class="flex md:hidden px-20 py-15 leading-none items-center bg-primary-100 border border-primary-300"
            :class="{ active: showFilter }"
            @click="showFilter = !showFilter"
          >
            <span class="mr-10">{{ $t('products.filter') }}</span>
            <plusIcon
              class="transition text-primary-300 -translate-y-1"
              :class="{ 'rotate-[135deg]': showFilter }"
            />
          </button>
        </div>

        <div class="relative">
          <button
            class="flex md:hidden px-20 py-15 leading-none items-center bg-primary-100 border border-primary-300"
            :class="{ active: showSort }"
            @click="showSort = !showSort"
          >
            <span class="mr-10">{{ $t('products.sort') }}</span>
            <plusIcon
              class="transition text-primary-300 -translate-y-1"
              :class="{ 'rotate-180': showSort }"
            />
          </button>
          <ul
            :class="{ hidden: !showSort }"
            class="bg-primary-100 border border-primary-300 absolute z-20 -mt-1 md:mt-0 right-0 whitespace-nowrap text-sm text-right p-10 lg:px-20 space-y-5"
          >
            <li>
              <button
                class="hover:underline"
                :class="{ 'font-bold': variables.orderBy === 'productPopular desc' }"
                @click="variables.orderBy = 'productPopular desc'"
              >
                {{ $t('products.popularity') }}
              </button>
            </li>
            <li>
              <button
                class="hover:underline"
                :class="{ 'font-bold': variables.orderBy === 'dateCreated desc' }"
                @click="variables.orderBy = 'dateCreated desc'"
              >
                {{ $t('products.added') }}
              </button>
            </li>
            <li>
              <button
                class="hover:underline"
                :class="{ 'font-bold': variables.orderBy === 'productPrice desc' }"
                @click="variables.orderBy = 'productPrice desc'"
              >
                {{ $t('products.price-asc') }}
              </button>
            </li>
            <li>
              <button
                class="hover:underline"
                :class="{ 'font-bold': variables.orderBy === 'productPrice asc' }"
                @click="variables.orderBy = 'productPrice asc'"
              >
                {{ $t('products.price-desc') }}
              </button>
            </li>
          </ul>
        </div>
      </div>

      <ul
        :class="{ hidden: !showFilter }"
        class="absolute z-20 divide-y divide-primary-300 md:divide-y-0 -mt-1 md:mt-0 inset-x-15 md:inset-x-0 md:relative md:flex text-sm lg:text-base items-center bg-primary-100 border border-primary-300"
      >
        <li
          v-if="showCategoryFilter"
          class="relative"
        >
          <button
            class="py-20 px-10 lg:p-20 leading-none flex items-center hover:underline"
            :class="{ active: showSubfilter.val === 'category' }"
            @click="toggleFilter('category', showSubfilter)"
          >
            <span class="mr-10">{{ $t('products.type') }}</span>
            <chevronIcon
              class="text-primary-300 w-20 h-20 transition"
              :class="{ 'rotate-180': showSubfilter.val === 'category' }"
            />
          </button>
          <ul
            :class="{ hidden: showSubfilter.val !== 'category' }"
            class="bg-primary-100 md:border md:border-t-0 border-primary-300 md:absolute left-0 md:-ml-1 p-10 lg:px-20 space-y-5"
          >
            <li>
              <label
                for="all"
                class="flex items-baseline hover:underline cursor-pointer"
              >
                <input
                  id="all"
                  name="alles"
                  type="checkbox"
                  class="mr-10"
                  @input="checkAll($event, filters, variables)"
                >
                <span>{{ $t('general.all') }}</span>
              </label>
            </li>
            <li
              v-for="filter in filters.categories"
              :key="`filter-${filter.id}`"
            >
              <label
                :for="`filter-${filter.id}`"
                class="flex items-baseline hover:underline cursor-pointer"
              >
                <input
                  :id="`filter-${filter.id}`"
                  v-model="variables.groups"
                  type="checkbox"
                  class="mr-10"
                  :value="filter.uri"
                >
                <span>{{ filter.title }}</span>
              </label>
            </li>
          </ul>
        </li>
        <li class="relative">
          <button
            class="py-20 px-10 lg:p-20 leading-none flex items-center hover:underline"
            :class="{ active: showSubfilter.val === 'delivery' }"
            @click="toggleFilter('delivery', showSubfilter)"
          >
            <span class="mr-10">{{ $t('products.delivery') }}</span>
            <chevronIcon
              class="text-primary-300 w-20 h-20 transition"
              :class="{ 'rotate-180': showSubfilter.val === 'delivery' }"
            />
          </button>
          <ul
            :class="{ hidden: showSubfilter.val !== 'delivery' }"
            class="bg-primary-100 md:border md:border-t-0 border-primary-300 md:absolute left-0 md:-ml-1 p-10 lg:px-20 space-y-5"
          >
            <li>
              <label
                for="soon"
                class="flex items-baseline hover:underline cursor-pointer"
              >
                <input
                  id="soon"
                  v-model="variables.delivery"
                  name="soon"
                  type="checkbox"
                  class="mr-10"
                  value="<=2"
                >
                <span>{{ $t('products.delivery-soon') }}</span>
              </label>
            </li>
            <li>
              <label
                for="later"
                class="flex items-baseline hover:underline cursor-pointer"
              >
                <input
                  id="later"
                  v-model="variables.delivery"
                  name="later"
                  type="checkbox"
                  class="mr-10"
                  value=">2"
                >
                <span>{{ $t('products.delivery-later') }}</span>
              </label>
            </li>
          </ul>
        </li>
        <!-- @TODO material filter -->
        <!-- <li class="relative">
          <button
            class="py-20 px-10 lg:p-20 leading-none flex items-center hover:underline"
            :class="{ active: showSubfilter === 'material' }"
            @click="toggleFilter('material')"
          >
            <span class="mr-10">{{ $t('products.material') }}</span>
            <chevronIcon
              class="text-primary-300 w-20 h-20 transition"
              :class="{ 'rotate-180': showSubfilter === 'material' }"
            />
          </button>
          <ul
            :class="{ hidden: showSubfilter !== 'material' }"
            class="bg-primary-100 md:border md:border-t-0 border-primary-300 md:absolute left-0 md:-ml-1 p-10 lg:px-20 space-y-5"
          >
          </ul>
        </li> -->
        <li class="relative">
          <button
            class="py-20 px-10 lg:p-20 leading-none flex items-center hover:underline"
            :class="{ active: showSubfilter.val === 'brand' }"
            @click="toggleFilter('brand', showSubfilter)"
          >
            <span class="mr-10">{{ $t('products.brand') }}</span>
            <chevronIcon
              class="text-primary-300 w-20 h-20 transition"
              :class="{ 'rotate-180': showSubfilter.val === 'brand' }"
            />
          </button>
          <ul
            :class="{ hidden: showSubfilter.val !== 'brand' }"
            class="bg-primary-100 md:border md:border-t-0 border-primary-300 md:absolute left-0 md:-ml-1 p-10 lg:px-20 space-y-5"
          >
            <li
              v-for="brand in filters.entries"
              :key="`brand-${brand.id}`"
            >
              <label
                :for="`brand-${brand.id}`"
                class="flex items-baseline hover:underline cursor-pointer"
              >
                <input
                  :id="`brand-${brand.id}`"
                  v-model="variables.brands"
                  type="checkbox"
                  class="mr-10"
                  :value="brand.uri"
                >
                <span>{{ brand.title }}</span>
              </label>
            </li>
          </ul>
        </li>
        <li class="relative ml-auto hidden md:block">
          <button
            class="py-20 px-10 lg:p-20 leading-none flex items-center hover:underline"
            :class="{ active: showSubfilter.val === 'sort' }"
            @click="toggleFilter('sort', showSubfilter)"
          >
            <span class="mr-10">{{ $t('products.sort') }}</span>
            <chevronIcon
              class="text-primary-300 w-20 h-20 transition"
              :class="{ 'rotate-180': showSubfilter.val === 'sort' }"
            />
          </button>
          <ul
            :class="{ hidden: showSubfilter.val !== 'sort' }"
            class="bg-primary-100 border border-t-0 border-primary-300 absolute right-0 whitespace-nowrap text-right -mr-1 p-10 lg:px-20 space-y-5"
          >
            <li>
              <button
                class="hover:underline"
                :class="{ 'font-bold': variables.orderBy === 'productPopular desc' }"
                @click="variables.orderBy = 'productPopular desc'"
              >
                {{ $t('products.popularity') }}
              </button>
            </li>
            <li>
              <button
                class="hover:underline"
                :class="{ 'font-bold': variables.orderBy === 'dateCreated desc' }"
                @click="variables.orderBy = 'dateCreated desc'"
              >
                {{ $t('products.added') }}
              </button>
            </li>
            <li>
              <button
                class="hover:underline"
                :class="{ 'font-bold': variables.orderBy === 'productPrice desc' }"
                @click="variables.orderBy = 'productPrice desc'"
              >
                {{ $t('products.price-asc') }}
              </button>
            </li>
            <li>
              <button
                class="hover:underline"
                :class="{ 'font-bold': variables.orderBy === 'productPrice asc' }"
                @click="variables.orderBy = 'productPrice asc'"
              >
                {{ $t('products.price-desc') }}
              </button>
            </li>
          </ul>
        </li>
      </ul>
    </div>

    <section class="mb-100">
      <div class="container">
        <div class="flex flex-wrap -mx-10 lg:-mx-15">
          <template
            v-for="(product, index) in products?.products"
            :key="`product-${product.id}`"
          >
            <div class="w-1/2 md:w-1/3 px-10 lg:px-15 mt-50">
              <product
                :product="product"
                :index="index"
                :lazy="index > 3"
              />
            </div>
            <div
              v-if="index === 3"
              :key="`banner-${index}`"
              class="w-full md:w-2/3 px-10 lg:px-15 mt-50"
            >
              <banner :banner="products.banner" />
            </div>
          </template>
        </div>
        <ClientOnly>
          <InfiniteLoading
            :key="route.query"
            :distance="300"
            :first-load="false"
            @infinite="infinite"
          >
            <template #spinner>
              <div class="flex justify-center mt-100">
                <img
                  src="@/assets/img/icon-loader.png"
                  alt="loading..."
                  class="w-[60px] h-[60px] animate-spin"
                >
              </div>
            </template>
            <template #complete>
              <span />
            </template>
            <template #error="{ retry }">
              <div class="flex justify-center mt-100">
                {{ $t('general.error') }}
                <button @click="retry">
                  &nbsp;{{ $t('general.retry') }}
                </button>
              </div>
            </template>
          </InfiniteLoading>
        </ClientOnly>
        <div
          v-if="products?.entry?.text && showCategoryFilter"
          class="wysiwyg mx-auto max-w-5xl my-50 md:my-75"
          v-html="products.entry.text"
        />
        <div
          v-if="products?.category?.text && !showCategoryFilter"
          class="wysiwyg mx-auto max-w-5xl my-50 md:my-75"
          v-html="products.category.text"
        />
      </div>
    </section>
  </div>
</template>

<script setup>
import InfiniteLoading from 'v3-infinite-loading';
import {
  navigateTo,
  ref,
  useRoute,
  useI18n,
  GqlFilters,
  GqlProducts,
  GqlProductIds,
  useState,
  watch,
  useSeo,
  definePageMeta,
  useDataLayer,
  useNuxtApp,
  useAsyncData,
  onMounted,
} from '#imports';

// General icons
import chevronIcon from '@/assets/img/icon-chevron.svg';
import plusIcon from '@/assets/img/icon-plus.svg';

// Custom data
const showSubfilter = useState('showSubfilter', () => ({ val: '' }));
const showCategoryFilter = useState('showCategoryFilter', () => false);
const showFilter = useState('showFilter', () => false);
const showSort = useState('showSort', () => false);
const filters = useState('filters', () => ({ categories: [] }));

// Data
const route = useRoute();
const { locale } = useI18n({ useScope: 'global' });
const routeUri = route.path.substring(1).replace(/\/+$/, '');
const { item } = useDataLayer();
const { $gtm } = useNuxtApp();
let uri = routeUri;
showCategoryFilter.value = uri.split('/').length === 1;
if (showCategoryFilter.value) {
  uri = `${uri}/*`;
}

const variables = ref({
  site: locale.value,
  routeUri,
  uri,
  limit: 7,
  groups: route.query.groups ?? [],
  delivery: route.query.delivery ?? [],
  orderBy: route.query.orderBy ?? '',
  brands: route.query.brands ?? [],
});
const offset = ref(0);

const { data: products } = await useAsyncData('products', async () => {
  offset.value = 0;

  const query = await GqlProducts({ ...variables.value, offset: offset.value });

  if (filters.value.categories.length === 0 && query.category) {
    const productIds = await GqlProductIds({ site: locale.value, uri });
    const filterData = await GqlFilters({ site: locale.value, groupHandle: query.category.groupHandle, relatedToProducts: productIds?.products.map((p) => p.id) });
    filters.value = filterData;
  }

  if (query?.entry?.seomatic || query?.category?.seomatic) {
    useSeo(query.entry?.seomatic ?? query.category.seomatic);
  }

  return query;
}, { watch: () => route.query });

// Lifecycle hooks
onMounted(() => {
  $gtm.trackEvent({ ecommerce: null });
  $gtm.trackEvent({
    event: 'view_item_list',
    ecommerce: {
      items: products.value?.products.map((product, index) => item({
        product,
        index,
        data: {
          item_list_id: 'products',
          item_list_name: 'Products',
        },
      })),
    },
  });
});

// Watchers
watch(variables, () => navigateTo({
  path: route.path,
  query: {
    groups: variables.value.groups,
    delivery: variables.value.delivery,
    orderBy: variables.value.orderBy,
    brands: variables.value.brands,
  },
}), { deep: true });

// Methods
/**
 * Infinite loader
 *
 * @param {Object} $state
 */
const infinite = async ($state) => {
  try {
    // Run query again with offset and limit
    offset.value += variables.value.limit;
    const result = await GqlProducts({ ...variables.value, offset: offset.value });

    // Append data
    products.value?.products.push(...result.products);

    // GTM event
    $gtm.trackEvent({ ecommerce: null });
    $gtm.trackEvent({
      event: 'view_item_list',
      ecommerce: {
        items: products.value?.products.map((product, index) => item({
          product,
          index,
          data: {
            item_list_id: 'products',
            item_list_name: 'Products',
          },
        })),
      },
    });

    // Set loading state
    if (result.products?.length < variables.value.limit) {
      $state.complete();
    } else {
      $state.loaded();
    }
  } catch (e) {
    if (process.env.NODE_ENV !== 'test') console.error(e); // eslint-disable-line no-console
    $state.error();
  }
};

// Meta
definePageMeta({
  keepalive: false,
});
</script>
